// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ConvertToNumeralFormat.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import numeral from 'numeral';
import { isNaN, isEmpty, isString, sum } from 'lodash/fp';

const FORMAT_TYPE = {
  /* key: Format of Izenda defined*/ /* value: Format of library Numeral.js*/
  /* Number */
  '0.00': '0.00',
  '0,000.00': '0,000.00',
  '0000': '0000',

  /* Currency */
  '$0.00': '$0.00',
  '$0,000.00': '$0,000.00',
  $0000: '$0000',
  '$/100': '$0.00',

  /* Percentage */
  '0.00%': '0.00%',
  '0,000.00%': '0,000.00%',
  '0000%': '0%',

  /* Grouping abbreviation styles : '1K', '1M', '1B' */
  Abbreviation: '0,000.00'
};

const PERCENTAGE_GROUP = '% of Group';
const PERCENTAGE_GROUP_ROUNDED = '% of Group (with rounding)';
const PERCENTAGE_SUBTOTAL = '% of Subtotal';
const PERCENTAGE_SUBTOTAL_ROUNDED = '% of Subtotal (with rounding)';
const PERCENTAGE_GRANDTOTAL = '% of Grandtotal';
const PERCENTAGE_GRANDTOTAL_ROUNDED = '% of Grandtotal (with rounding)';
const PERCENTAGE_SIDETOTAL = '% of Sidetotal';
const PERCENTAGE_SIDETOTAL_ROUNDED = '% of Sidetotal (with rounding)';
const NO_DEVIDEND = 1;
const HUNDRED_DEVIDEND = 100;
const THOUSAND = '1K';
const THOUSAND_DEVIDEND = 1000;
const MILLION = '1M';
const MILLION_DEVIDEND = 1000000;
const BILLION = '1B';
const BILLION_DEVIDEND = 1000000000;
const DECIMAL_POINT = 8;
const ROUNDED_DECIMAL_POINT = 1;
const DEFAULT_VALUE = 0;
const PERCENT_DIVIDEND = 100;

// Calcuate the percentage of value/array value
function calculatedPercentageGroup(value: number, arrayValue: Array<number>) {
  if (isEmpty(arrayValue)) {
    return value;
  }

  return (value / sum(arrayValue)) * PERCENT_DIVIDEND;
}

function formatNumberWithDevidend(value: number, dividendNumber: number, formatType: string, symbol: string) {
  const absValue = Math.abs(value / dividendNumber);
  const isNegative = value < 0;

  return `${isNegative ? '-' : ''}${numeral(absValue).format(FORMAT_TYPE[formatType])}${symbol}`;
}

function formatPercent(value: number, decimalPoint: number) {
  const eValue = Number(value + `e+${decimalPoint}`);
  return `${+(Math.round(eValue) + `e-${decimalPoint}`)}%`;
}

/**
 * Convert to numeral format
 * @param {string} pattern format pattern
 * @param {number} orgVal original value
 * @param {Array<number>} arrayValue this is optional parameter 
 *                                   when pattern is '% of Group' or '% of Group (with rounding)'
 */
export default function(pattern: string, orgVal: number, arrayValue: Array<number>) {
  if ((isString(orgVal) && isEmpty(orgVal)) || isNaN(Number(orgVal)) || isEmpty(pattern)) {
    return orgVal;
  }

  const value = Number(orgVal || DEFAULT_VALUE).toFixed(DECIMAL_POINT);

  switch (pattern) {
    case PERCENTAGE_GROUP:
    case PERCENTAGE_SUBTOTAL:
    case PERCENTAGE_GRANDTOTAL:
    case PERCENTAGE_SIDETOTAL:
      return formatPercent(calculatedPercentageGroup(value, arrayValue), DECIMAL_POINT);
    case PERCENTAGE_GROUP_ROUNDED:
    case PERCENTAGE_SUBTOTAL_ROUNDED:
    case PERCENTAGE_GRANDTOTAL_ROUNDED:
    case PERCENTAGE_SIDETOTAL_ROUNDED:
      return formatPercent(calculatedPercentageGroup(value, arrayValue), ROUNDED_DECIMAL_POINT);
    case THOUSAND:
      return formatNumberWithDevidend(value, THOUSAND_DEVIDEND, 'Abbreviation', 'K');
    case MILLION:
      return formatNumberWithDevidend(value, MILLION_DEVIDEND, 'Abbreviation', 'M');
    case BILLION:
      return formatNumberWithDevidend(value, BILLION_DEVIDEND, 'Abbreviation', 'B');
    case '$/100':
      return formatNumberWithDevidend(value, HUNDRED_DEVIDEND, pattern, '');
    case '0.00%':
    case '0,000.00%':
    case '0000%':
      return formatNumberWithDevidend(value, PERCENT_DIVIDEND, pattern, '');
    default:
      return FORMAT_TYPE[pattern] ? formatNumberWithDevidend(value, NO_DEVIDEND, pattern, '') : orgVal;
  }
}



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.ConvertToNumeralFormat.js